import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface AboutProps {
  darkMode: boolean;
}

const About: React.FC<AboutProps> = (/*{ darkMode }*/) => {
  return (
    <>
      <Helmet>
        <title>About YouTube Player - Chat with Your Videos</title>
        <meta name="description" content="YouTube Player: Chat with your videos using AI. Get instant answers, summaries, and insights from any YouTube video's transcript." />
        <meta name="keywords" content="about youtube player, video chat, ai chat, youtube transcript, video assistant" />
        <meta property="og:title" content="About YouTube Player - Chat with Your Videos" />
        <meta property="og:description" content="YouTube Player: Chat with your videos using AI. Get instant answers, summaries, and insights from any YouTube video's transcript." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="/about" />
      </Helmet>

      <div className="max-w-3xl mx-auto">
        {/* Back to Home Button - Using new palette */}
        <Link 
          to="/"
          className="inline-flex items-center mb-8 px-4 py-2 rounded-lg text-sm font-medium 
                     bg-black/5 text-light-subtle dark:bg-white/10 dark:text-dark-subtle 
                     hover:bg-black/10 dark:hover:bg-white/20 
                     transition-colors duration-200"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Home
        </Link>

        {/* Custom styles for the article content */}
        <article className="max-w-none space-y-6">
          <h1 className="text-3xl font-bold mb-4 text-light-text dark:text-dark-text">
            About YouTube Player
          </h1>
          
          <p className="text-lg mb-6 text-light-subtle dark:text-dark-subtle">
            We built YouTube Player to help you interact with your videos in a whole new way. Chat with any YouTube video and get instant answers, summaries, and insights.
          </p>

          <h2 className="text-2xl font-semibold mb-4 text-light-text dark:text-dark-text">
            Why Choose Us?
          </h2>

          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2 text-light-text dark:text-dark-text">
              AI-Powered Video Chat
            </h3>
            <p className="text-light-text dark:text-dark-text">
              Have natural conversations with any YouTube video. Ask questions, get summaries, or dive deep into specific topics. Our AI understands the video's content and provides relevant, contextual responses.
            </p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2 text-light-text dark:text-dark-text">
              Clean & Simple Design
            </h3>
            <p className="text-light-text dark:text-dark-text">
              Get a distraction-free interface that works beautifully on any device. Choose dark or light mode for 
              comfortable viewing, day or night. No clutter, just your content and conversation.
            </p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-semibold mb-2 text-light-text dark:text-dark-text">
              Total Privacy
            </h3>
            <p className="text-light-text dark:text-dark-text">
              No accounts, no tracking, no cookies. Your conversations stay private, period. 
              In a world where data is constantly collected, we offer a truly anonymous experience.
            </p>
          </section>

          <p className="mt-8 text-lg font-medium text-light-text dark:text-dark-text">
            Experience a new way of interacting with your favorite videos.
          </p>
        </article>
      </div>
    </>
  );
};

export default About;